import React, { useState, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, Sparkles, Loader2, Copy, Check } from 'lucide-react';
import { AIChatMessage } from './AIChatMessage';
import { ActionCardRenderer } from './action-cards/ActionCardRenderer';
import { getActionCardConfig } from './action-cards/actionCardRegistry';
import { ClarificationDialog } from './ClarificationDialog';
import { DataDiscoveryModal } from './DataDiscoveryModal';
import { RemoveDataConfirmModal } from './action-cards/RemoveDataConfirmModal';
import { StyleIntelligenceModal } from './StyleIntelligenceModal';
import { IconPickerModal } from '@/components/modals/IconPickerModal';
import { ColorPickerModal } from '@/components/modals/ColorPickerModal';
import { DeviceBindingPicker } from '@/components/shared/DeviceBindingPicker';
import type { DeviceBinding } from '@/components/shared/rendering/InteractionContext';
import { HomeAssistantEntityBindingModal } from './HomeAssistantEntityBindingModal';
import { ComponentType, IconLibrary, Component, Page } from '@/types';
import { memoriesApi } from '@/services/api/agentApi';
import { decodeHtmlEntities } from '@/utils/htmlEntities';
import ReactMarkdown, { type Components } from 'react-markdown';
import type {
  ClarificationRequest,
  ClarificationResponse,
} from '@/types/ai';
import type { PropertyMessage, ComponentData, LayoutData } from './types';
import type { ComponentUpdatePayload } from '@/types/component-update';
import type { UseDataDiscoveryReturn } from './useDataDiscovery';
import type { UseStyleIntelligenceReturn } from './useStyleIntelligence';
import type { ColorSchemeWriteTarget } from '@/types/color-scheme';
import { useEditorStore } from '@/stores/editor/editor-store';

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : undefined;

const isIconLibrary = (value: unknown): value is IconLibrary =>
  typeof value === 'string' && Object.values(IconLibrary).some((library) => library === value);

const isDeviceBinding = (value: unknown): value is DeviceBinding => {
  if (!isRecord(value) || !isRecord(value.capabilities)) return false;

  return value.type === 'kasa' &&
    typeof value.deviceId === 'string' &&
    typeof value.deviceName === 'string' &&
    typeof value.controllerDeviceId === 'string' &&
    typeof value.orgSlug === 'string' &&
    typeof value.capabilities.toggle === 'boolean' &&
    typeof value.capabilities.brightness === 'boolean';
};

const getComponentDisplayName = (component: Component | null): string => {
  if (!component) return 'Component';

  return readString(component.properties.title) ||
    readString(component.properties.name) ||
    readString(component.properties.label) ||
    component.type ||
    'Component';
};

interface MarkdownElementProps extends React.ClassAttributes<HTMLElement>, React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  node?: unknown;
}

const markdownComponents: Components = {
  p: ({ children }: MarkdownElementProps) => (
    <p style={{ margin: '0 0 8px 0', lineHeight: 1.6 }}>{children}</p>
  ),
  ul: ({ children }: MarkdownElementProps) => (
    <ul style={{ margin: '4px 0 8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>{children}</ul>
  ),
  ol: ({ children }: MarkdownElementProps) => (
    <ol style={{ margin: '4px 0 8px 0', paddingLeft: '20px', listStyleType: 'decimal' }}>{children}</ol>
  ),
  li: ({ children }: MarkdownElementProps) => (
    <li style={{ margin: '2px 0', lineHeight: 1.5, display: 'list-item' }}>{children}</li>
  ),
  strong: ({ children }: MarkdownElementProps) => (
    <strong style={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>{children}</strong>
  ),
  code: ({ children, className }: MarkdownElementProps) => {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <pre style={{
        backgroundColor: 'hsl(var(--muted))',
        borderRadius: '4px',
        padding: '8px 12px',
        margin: '8px 0',
        overflow: 'auto',
        fontSize: '13px',
      }}>
        <code>{children}</code>
      </pre>
    ) : (
      <code style={{
        backgroundColor: 'hsl(var(--muted))',
        borderRadius: '3px',
        padding: '1px 4px',
        fontSize: '13px',
      }}>{children}</code>
    );
  },
  table: ({ children }: MarkdownElementProps) => (
    <table style={{
      borderCollapse: 'collapse',
      width: '100%',
      margin: '8px 0',
      fontSize: '13px',
    }}>{children}</table>
  ),
  th: ({ children }: MarkdownElementProps) => (
    <th style={{
      borderBottom: '2px solid hsl(var(--border))',
      padding: '4px 8px',
      textAlign: 'left',
      fontWeight: 600,
    }}>{children}</th>
  ),
  td: ({ children }: MarkdownElementProps) => (
    <td style={{
      borderBottom: '1px solid hsl(var(--border))',
      padding: '4px 8px',
    }}>{children}</td>
  ),
};

/** Small copy-to-clipboard button shown on message hover */
const MessageCopyButton: React.FC<{ text: string; isUserMessage: boolean }> = ({ text, isUserMessage }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may not be available */ }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      aria-label="Copy message"
      title="Copy message"
      style={{
        position: 'absolute',
        bottom: 4,
        right: 4,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        backgroundColor: isUserMessage ? 'rgba(255,255,255,0.15)' : 'hsl(var(--muted))',
        transition: 'opacity 150ms ease',
      }}
    >
      {copied ? (
        <Check size={14} color={isUserMessage ? 'white' : 'hsl(var(--primary))'} />
      ) : (
        <Copy size={14} color={isUserMessage ? 'white' : 'hsl(var(--muted-foreground))'} />
      )}
    </button>
  );
};

export interface ChatMessageListProps {
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  messages: PropertyMessage[];
  component: Component | null;
  messageFeedback: Record<string, 'positive' | 'negative'>;
  setMessageFeedback: React.Dispatch<React.SetStateAction<Record<string, 'positive' | 'negative'>>>;
  setMessages: React.Dispatch<React.SetStateAction<PropertyMessage[]>>;
  storePages: Page[];
  currentPageId: string | null | undefined;
  generateMessageId: () => string;
  handlePlaceComponent: (data: ComponentData) => Promise<boolean>;
  handleApplyLayout: (data: LayoutData) => void;
  autoSendCommand: (command: string, styleWriteTarget?: ColorSchemeWriteTarget) => void;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  dataDiscovery: UseDataDiscoveryReturn;
  setRemoveDataModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIconPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenMapTemplates?: () => void;
  setColorCommandPrefix: React.Dispatch<React.SetStateAction<string>>;
  setColorPropertyLabel: React.Dispatch<React.SetStateAction<string>>;
  setColorPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDeviceBindingOpen: React.Dispatch<React.SetStateAction<boolean>>;
  homeAssistantBindingOpen: boolean;
  setHomeAssistantBindingOpen: React.Dispatch<React.SetStateAction<boolean>>;
  updateComponent: (id: string, updates: Partial<ComponentUpdatePayload>) => void;
  duplicateComponent?: (id: string) => void;
  pendingClarification: ClarificationRequest | null;
  handleClarificationResponse: (response: ClarificationResponse) => Promise<void>;
  handleClarificationCancel: () => void;
  isLoading: boolean;
  removeDataModalOpen: boolean;
  removeDataSourceLabel: string | null;
  handleRemoveDataConfirm: () => void;
  iconPickerOpen: boolean;
  colorPickerOpen: boolean;
  colorPropertyLabel: string;
  colorCommandPrefix: string;
  deviceBindingOpen: boolean;
  planOrchestrator: { handlePlanMessage: (input: string) => Promise<void> } | null;
  styleIntelligence: UseStyleIntelligenceReturn;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  scrollAreaRef,
  messages,
  component,
  messageFeedback,
  setMessageFeedback,
  setMessages,
  storePages,
  currentPageId,
  generateMessageId,
  handlePlaceComponent,
  handleApplyLayout,
  autoSendCommand,
  setInputValue,
  inputRef,
  dataDiscovery,
  setRemoveDataModalOpen,
  setIconPickerOpen,
  onOpenMapTemplates,
  setColorCommandPrefix,
  setColorPropertyLabel,
  setColorPickerOpen,
  setDeviceBindingOpen,
  homeAssistantBindingOpen,
  setHomeAssistantBindingOpen,
  updateComponent,
  duplicateComponent,
  pendingClarification,
  handleClarificationResponse,
  handleClarificationCancel,
  isLoading,
  removeDataModalOpen,
  removeDataSourceLabel,
  handleRemoveDataConfirm,
  iconPickerOpen,
  colorPickerOpen,
  colorPropertyLabel,
  colorCommandPrefix,
  deviceBindingOpen,
  planOrchestrator,
  styleIntelligence,
}) => {
  return (
      <div ref={scrollAreaRef} className="flex-1 min-h-0 overflow-y-auto">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', minWidth: 0 }}>
          {messages.map((message, index) => {
            // For complex messages with component/layout data, use AIChatMessage
            if (message.componentData || message.layoutData) {
              return (
                <AIChatMessage
                  key={message.id}
                  message={message}
                  isLatest={index === messages.length - 1}
                  onPlaceComponent={handlePlaceComponent}
                  onApplyLayout={handleApplyLayout}
                />
              );
            }

            // For simple messages, use inline rendering with brand colors
            // Detect action card messages using registry markers
            const isActionCardMessage = message.role === 'assistant' && component &&
              (() => {
                const config = getActionCardConfig(component.type);
                if (!config) return false;
                if (message.content.includes(config.marker)) return true;
                // MAP_CHART with pin selected uses a different marker
                if (component.type === ComponentType.MAP_CHART && message.content.includes('◈ Pin selected:')) return true;
                return false;
              })();

            const isAgentMessage = message.role === 'assistant'
              && !!message.metadata?.source;

            return (
              <div
                key={message.id}
                className="flex"
                style={{
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <div
                  className="group"
                  style={{
                    maxWidth: '85%',
                    minWidth: 0,
                    borderRadius: '8px',
                    padding: '10px 14px',
                    position: 'relative',
                    backgroundColor: message.role === 'user'
                      ? 'hsl(var(--primary))'
                      : 'hsl(var(--muted))',
                    color: message.role === 'user' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--card-foreground))',
                    border: message.role === 'user'
                      ? '1px solid transparent'
                      : '1px solid hsl(var(--border))',
                    boxShadow: message.role === 'user'
                      ? '0 1px 2px hsl(var(--primary) / 0.18)'
                      : '0 1px 2px hsl(var(--foreground) / 0.06)',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {isActionCardMessage && component ? (
                    <ActionCardRenderer
                      component={component}
                      pages={storePages ?? []}
                      currentPageId={currentPageId ?? null}
                      updateComponent={updateComponent}
                      onAutoSend={autoSendCommand}
                      onPreFill={(text, styleWriteTarget) => {
                        if (styleWriteTarget) {
                          useEditorStore.getState().setStyleWriteTarget(styleWriteTarget);
                        }
                        setInputValue(text);
                        inputRef.current?.focus();
                      }}
                      onInjectMessage={(content) => {
                        setMessages(prev => [...prev, {
                          id: generateMessageId(),
                          content,
                          role: 'assistant',
                          timestamp: new Date(),
                        }]);
                      }}
                      onDataDiscovery={() => dataDiscovery.openModal()}
                      onRemoveData={() => setRemoveDataModalOpen(true)}
                      onIconPicker={() => setIconPickerOpen(true)}
                      onMapTemplates={onOpenMapTemplates}
                      onColorPicker={(prefix, label, styleWriteTarget) => {
                        if (styleWriteTarget) {
                          useEditorStore.getState().setStyleWriteTarget(styleWriteTarget);
                        }
                        setColorCommandPrefix(prefix);
                        setColorPropertyLabel(label);
                        setColorPickerOpen(true);
                      }}
                      onDeviceBinding={() => setDeviceBindingOpen(true)}
                      onHomeAssistantBinding={() => setHomeAssistantBindingOpen(true)}
                      onDuplicateComponent={duplicateComponent && component
                        ? () => {
                            duplicateComponent(component.id);
                            setMessages(prev => [...prev, {
                              id: generateMessageId(),
                              content: `Duplicated "${component.name || component.type}".`,
                              role: 'assistant',
                              timestamp: new Date(),
                            }]);
                          }
                        : undefined}
                      onHideComponent={component
                        ? () => {
                            updateComponent(component.id, { hidden: !component.hidden });
                            setMessages(prev => [...prev, {
                              id: generateMessageId(),
                              content: `${component.hidden ? 'Shown' : 'Hidden'} "${component.name || component.type}".`,
                              role: 'assistant',
                              timestamp: new Date(),
                            }]);
                          }
                        : undefined}
                    />
                  ) : isAgentMessage ? (
                    <div style={{ fontSize: '14px', margin: 0, lineHeight: 1.6 }}>
                      <ReactMarkdown
                        components={markdownComponents}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p style={{ fontSize: '14px', whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
                      {decodeHtmlEntities(message.content)}
                    </p>
                  )}
                  {/* Questions are rendered as conversational text in the message body — no card UI */}
                  {/* Fallback: plan-mode assistant message that's not planReady */}
                  {message.role === 'assistant'
                    && message.metadata?.source === 'plan-mode'
                    && !message.metadata?.planReady
                    && !message.metadata?.questionsAnswered
                    && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          onClick={() => {
                            // Mark as answered so the button disappears
                            setMessages((prev) =>
                              prev.map((m) =>
                                m.id === message.id
                                  ? { ...m, metadata: { ...m.metadata, questionsAnswered: true } }
                                  : m,
                              ),
                            );
                            if (planOrchestrator) {
                              planOrchestrator.handlePlanMessage('continue').catch(() => undefined);
                            }
                          }}
                          style={{
                            padding: '6px 16px',
                            borderRadius: 6,
                            border: 'none',
                            background: 'hsl(var(--primary))',
                            color: 'hsl(var(--primary-foreground))',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          Continue
                        </button>
                      </div>
                    )}
                  {/* Agent badge with model info + feedback buttons */}
                  {isAgentMessage && !message.isStreaming && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '4px',
                      marginTop: '6px',
                      paddingTop: '6px',
                      borderTop: '1px solid hsl(var(--border))',
                    }}>
                      {/* Memory feedback buttons */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <button
                          onClick={() => {
                            if (messageFeedback[message.id]) return;
                            setMessageFeedback(prev => ({ ...prev, [message.id]: 'positive' }));
                            memoriesApi.submitFeedback({
                              thread_id: typeof message.metadata?.threadId === 'string' ? message.metadata.threadId : '',
                              message_id: message.id,
                              signal: 'positive',
                              memory_ids: readStringArray(message.metadata?.memoriesUsed),
                            }).catch(() => {});
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: messageFeedback[message.id] ? 'default' : 'pointer',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            opacity: messageFeedback[message.id] === 'negative' ? 0.3 : messageFeedback[message.id] === 'positive' ? 1 : 0.5,
                            transition: 'all 150ms ease',
                          }}
                          title="Helpful response"
                        >
                          <ThumbsUp size={12} color={messageFeedback[message.id] === 'positive' ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))'} />
                        </button>
                        <button
                          onClick={() => {
                            if (messageFeedback[message.id]) return;
                            setMessageFeedback(prev => ({ ...prev, [message.id]: 'negative' }));
                            memoriesApi.submitFeedback({
                              thread_id: typeof message.metadata?.threadId === 'string' ? message.metadata.threadId : '',
                              message_id: message.id,
                              signal: 'negative',
                              memory_ids: readStringArray(message.metadata?.memoriesUsed),
                            }).catch(() => {});
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: messageFeedback[message.id] ? 'default' : 'pointer',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            opacity: messageFeedback[message.id] === 'positive' ? 0.3 : messageFeedback[message.id] === 'negative' ? 1 : 0.5,
                            transition: 'all 150ms ease',
                          }}
                          title="Unhelpful response"
                        >
                          <ThumbsDown size={12} color={messageFeedback[message.id] === 'negative' ? 'hsl(var(--destructive))' : 'hsl(var(--muted-foreground))'} />
                        </button>
                      </div>
                    </div>
                  )}
                  {message.propertyChanges && message.applied && (
                    <p style={{ fontSize: '12px', marginTop: '4px', opacity: 0.75 }}>✓ Changes applied</p>
                  )}
                  {message.suggestions && message.suggestions.length > 0 && !message.isStreaming && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {message.suggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => {
                            autoSendCommand(suggestion);
                          }}
                          style={{
                            fontSize: '12px',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid hsl(var(--primary) / 0.3)',
                            backgroundColor: 'hsl(var(--primary) / 0.08)',
                            color: 'hsl(var(--primary))',
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                            fontWeight: 500,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'hsl(var(--primary))';
                            e.currentTarget.style.color = 'hsl(var(--primary-foreground))';
                            e.currentTarget.style.borderColor = 'hsl(var(--primary))';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'hsl(var(--primary) / 0.08)';
                            e.currentTarget.style.color = 'hsl(var(--primary))';
                            e.currentTarget.style.borderColor = 'hsl(var(--primary) / 0.3)';
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                  {message.isStreaming && (
                    message.content === 'Consulting agents...' ? (
                      <Sparkles size={16} className="animate-pulse mt-1 text-primary" />
                    ) : (
                      <Loader2 className="w-3 h-3 animate-spin mt-1" />
                    )
                  )}
                  {!message.isStreaming && !isActionCardMessage && (
                    <div
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      style={{ position: 'absolute', bottom: 4, right: 4 }}
                    >
                      <MessageCopyButton text={message.content} isUserMessage={message.role === 'user'} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Phase 1.2: Clarification Dialog - rendered inline after messages */}
          {pendingClarification && (
            <ClarificationDialog
              request={pendingClarification}
              onResponse={handleClarificationResponse}
              onCancel={handleClarificationCancel}
              isLoading={isLoading}
            />
          )}

          {/* Data Discovery Modal */}
          <DataDiscoveryModal {...dataDiscovery} component={component} />

          {/* Remove Data Confirmation Modal */}
          <RemoveDataConfirmModal
            isOpen={removeDataModalOpen}
            componentName={getComponentDisplayName(component)}
            dataSourceLabel={removeDataSourceLabel}
            onConfirm={handleRemoveDataConfirm}
            onCancel={() => setRemoveDataModalOpen(false)}
          />

          {/* Style Intelligence Modal */}
          <StyleIntelligenceModal {...styleIntelligence} />

          <HomeAssistantEntityBindingModal
            isOpen={homeAssistantBindingOpen}
            component={component}
            onClose={() => setHomeAssistantBindingOpen(false)}
            onBind={(updates) => {
              if (!component) return;
              updateComponent(component.id, updates);
              setMessages(prev => [...prev, {
                id: generateMessageId(),
                content: `Bound Home Assistant entity metadata to "${component.name || component.type}".`,
                role: 'assistant',
                timestamp: new Date(),
              }]);
            }}
            onUnbind={(updates) => {
              if (!component) return;
              updateComponent(component.id, updates);
              setMessages(prev => [...prev, {
                id: generateMessageId(),
                content: `Removed Home Assistant binding metadata from "${component.name || component.type}".`,
                role: 'assistant',
                timestamp: new Date(),
              }]);
            }}
          />

          {/* Icon Picker Modal */}
          <IconPickerModal
            isOpen={iconPickerOpen}
            onClose={() => setIconPickerOpen(false)}
            selectedIcon={(() => {
              if (!component) return undefined;
              if (component.type === ComponentType.DATA_CARD) return readString(component.properties.icon);
              return readString(component.properties.iconName);
            })()}
            selectedLibrary={(() => {
              if (!component) return undefined;
              if (component.type === ComponentType.DATA_CARD) return IconLibrary.LUCIDE;
              const library = component.type === ComponentType.BUTTON
                ? component.properties.iconLibrary
                : component.properties.library;
              return isIconLibrary(library) ? library : undefined;
            })()}
            libraryFilter={component?.type === ComponentType.DATA_CARD ? 'lucide' : undefined}
            onSelect={(iconName, library) => {
              if (!component) return;
              const props = { ...component.properties };
              if (component.type === ComponentType.DATA_CARD) {
                props.icon = iconName;
              } else if (component.type === ComponentType.BUTTON) {
                props.iconName = iconName;
                props.iconLibrary = library;
              } else {
                props.iconName = iconName;
                props.library = library;
              }
              updateComponent(component.id, { properties: props });
            }}
          />

          {/* Color Picker Modal */}
          <ColorPickerModal
            isOpen={colorPickerOpen}
            onClose={() => setColorPickerOpen(false)}
            propertyLabel={colorPropertyLabel}
            currentColor={(() => {
              if (!component) return undefined;
              return readString(component.style.color) ||
                readString(component.style.backgroundColor) ||
                readString(component.properties.color);
            })()}
            onSelectColor={(color) => {
              const styleWriteTarget = useEditorStore.getState().styleWriteTarget;
              autoSendCommand(`${colorCommandPrefix}${color}`, styleWriteTarget);
              useEditorStore.getState().setStyleWriteTarget('both');
              setColorPickerOpen(false);
            }}
          />

          {/* Device Binding Picker */}
          {deviceBindingOpen && component && (
            <div style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div
                style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }}
                onClick={() => setDeviceBindingOpen(false)}
              />
              <div style={{
                position: 'relative',
                background: 'var(--background, #fff)',
                borderRadius: '8px',
                padding: '20px',
                width: '340px',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>
                  Bind to Smart Device
                </h3>
                <DeviceBindingPicker
                  currentBinding={(() => {
                    const action = isRecord(component.properties.action) ? component.properties.action : null;
                    return isDeviceBinding(action?.deviceBinding) ? action.deviceBinding : undefined;
                  })()}
                  onSelect={(binding) => {
                    if (component) {
                      const props = { ...component.properties };
                      const action = isRecord(props.action) ? props.action : { type: 'sensor-control' };
                      props.action = {
                        ...action,
                        deviceBinding: binding,
                        deviceId: binding?.deviceId || '',
                      };
                      updateComponent(component.id, { properties: props });
                    }
                    setDeviceBindingOpen(false);
                  }}
                />
                <button
                  onClick={() => setDeviceBindingOpen(false)}
                  style={{
                    marginTop: '12px',
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: '6px',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Suggestions are rendered inline in message bubbles — no separate FollowUpSuggestions needed */}
        </div>
      </div>
  );
};
